import React, { useEffect, useMemo, useState } from 'react';
import { Rocket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { subscribeToReleaseNotes } from '../../services/articleService';
import type { Article } from '../../types';

/** Timestamp (ms) for et versjonsnotat. */
function noteMillis(a: Article): number {
  const raw: any = a.published_at ?? a.created_at;
  if (!raw) return 0;
  if (typeof raw.toMillis === 'function') return raw.toMillis();
  if (typeof raw.seconds === 'number') return raw.seconds * 1000;
  const n = typeof raw === 'number' ? raw : new Date(raw).getTime();
  return isNaN(n) ? 0 : n;
}

function localize(a: Article, lang: string): { title: string; summary: string } {
  const key = lang?.split('-')[0];
  const tr = key === 'en' || key === 'de' ? a.translations?.[key] : undefined;
  if (tr) return { title: tr.title, summary: tr.summary };
  return { title: a.title, summary: a.summary };
}

interface PublicWhatsNewTeaserProps {
  /** Maks antall notater å vise. */
  max?: number;
  className?: string;
}

/**
 * Offentlig «Nytt i Fricamp»-teaser — viser de siste publiserte versjonsnotatene.
 * Signaliserer til publikum at Fricamp er en levende, aktiv aktør. Fail-soft:
 * rendrer ingenting hvis det ikke finnes publiserte notater.
 */
const PublicWhatsNewTeaser: React.FC<PublicWhatsNewTeaserProps> = ({ max = 3, className = '' }) => {
  const { t, i18n } = useTranslation();
  const [notes, setNotes] = useState<Article[]>([]);

  useEffect(() => subscribeToReleaseNotes(setNotes, max), [max]);

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language || 'nb-NO', { day: 'numeric', month: 'short', year: 'numeric' }),
    [i18n.language]
  );

  if (notes.length === 0) return null;

  return (
    <section className={`rounded-3xl border border-emerald-500/20 bg-black/20 p-6 ${className}`}>
      <div className="flex items-center gap-2 text-emerald-300">
        <Rocket size={16} />
        <h2 className="text-lg font-black">{t('whatsNew.publicTitle', 'Nytt i Fricamp')}</h2>
      </div>
      <p className="mt-1 text-xs text-stone-400">{t('whatsNew.publicSubtitle', 'Vi utvikler Fricamp kontinuerlig. Her er det siste.')}</p>
      <ol className="mt-4 space-y-3">
        {notes.slice(0, max).map((note) => {
          const loc = localize(note, i18n.language);
          const ms = noteMillis(note);
          const href = note.slug ? `https://www.fricamp.com/blogg/${note.slug}` : undefined;
          const Inner = (
            <>
              <div className="flex items-center gap-2">
                {note.version && (
                  <span className="text-3xs font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/5 text-stone-300 border border-white/10">
                    v{note.version}
                  </span>
                )}
                <p className="text-sm font-black text-white">{loc.title}</p>
                {ms > 0 && <span className="ml-auto text-3xs font-bold text-stone-500">{dateFmt.format(new Date(ms))}</span>}
              </div>
              {loc.summary && <p className="mt-1 text-xs text-stone-300 leading-relaxed">{loc.summary}</p>}
            </>
          );
          return (
            <li key={note.id} className="rounded-xl border border-white/10 bg-white/5 p-4">
              {href ? (
                <a href={href} target="_blank" rel="noopener noreferrer" className="block hover:opacity-90 transition-opacity">
                  {Inner}
                </a>
              ) : Inner}
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default PublicWhatsNewTeaser;
